import submissionApi from 'src/services/submission.service.js'
import opportunitiesApi from 'src/services/opportunities.service.js'
import entityApi from 'src/services/entity.service.js'
import helpers from 'src/helpers/submission-helpers'
import {
  SET_SUBMISSIONS,
  SET_SUBMISSION_DATA,
  SET_SUBMISSION_ID,
  SET_INTERNAL_SUBMISSIONS,
  SELECT_PLATFORM,
  SET_COMPANY_DATA,
  SET_OPPORTUNITIES,
} from './mutation-types'
import AppConfig from 'src/app.config'

const RELEASE_STAGE = AppConfig.releaseStage

const state = {
  platforms: {},
  submissions: [],
  latestSubmission: {},
  selectedSubmissionId: 'latest',
  selectedPlatform: '',
  selectedCompanyData: {},
  internalSubmissions: [],
  isInternalLoaded: false,
}

const mutations = {
  [SET_SUBMISSIONS](state, data) {
    state.submissions = data
  },
  [SET_SUBMISSION_DATA](state, data) {
    state.latestSubmission = data
  },
  [SET_SUBMISSION_ID](state, payload) {
    state.selectedSubmissionId = payload
  },
  [SELECT_PLATFORM](state, payload) {
    state.selectedPlatform = payload.selectedPlatform
    state.selectedCompanyData = payload.companyData
  },
  [SET_COMPANY_DATA](state, payload) {
    state.selectedCompanyData = payload
  },
  [SET_OPPORTUNITIES](state, payload) {
    state.platforms = payload
  },
  [SET_INTERNAL_SUBMISSIONS](state, payload) {
    state.internalSubmissions = payload
    state.isInternalLoaded = true
  },
}

const actions = {
  setSubmissionId({ commit }, submissionId) {
    commit(SET_SUBMISSION_ID, submissionId)
  },
  clearSubmission({ commit }) {
    commit(SET_SUBMISSION_ID, 'latest')
    commit(SET_SUBMISSION_DATA, {})
  },
  getProposals({ commit, dispatch }) {
    return submissionApi.getSubmissions().then(res => {
      const submissions = _.get(res, 'data.items', []).map(item => {
        const obj = {
          ...item.lastSubmission,
          formatted: item.lastSubmissionFormatted,
          companyId: item.companyId,
          internalInterestId: item.interestId,
        }
        const opportunityCompanyId = _.get(
          obj,
          'formatted.company.opportunityCompanyId'
        )
        if (opportunityCompanyId) {
          obj.opportunityCompanyId = opportunityCompanyId
        }
        return obj
      })
      const ids = submissions
        .map(s => s.internalInterestId)
        .filter(id => id && id.length === 24)
      if (ids.length) {
        dispatch('getProposalsInternals', ids)
      }
      commit(SET_SUBMISSIONS, submissions)
    })
  },
  getProposalsInternals({ commit }, ids) {
    return entityApi.getEntitiesMulti(ids).then(res => {
      commit(SET_INTERNAL_SUBMISSIONS, res.data.items)
    })
  },
  getSubmission({ commit }, submissionId) {
    return submissionApi.getSubmission(submissionId).then(res => {
      const data = res.data
      if (_.isEmpty(data)) {
        return 'forbidden'
      }
      if (Object.keys(data).length) {
        if (data.dealroomInterestId) {
          commit(SET_SUBMISSION_ID, data.dealroomInterestId)
        }
        const normalizedData = helpers.normalizeDataForForm(data)
        commit(SET_SUBMISSION_DATA, normalizedData)
      }
    })
  },
  updateSubmission({ commit, state }, payload) {
    const normalized = _.cloneDeep(payload)
    const fundYear = normalized.interest.fundYear
    const fundMonth = normalized.interest.fundMonth
    normalized.interest.fundDate = helpers.formatFundDate(fundYear, fundMonth)
    if (
      state.selectedSubmissionId &&
      state.selectedSubmissionId.length === 24
    ) {
      normalized.dealroomInterestId = state.selectedSubmissionId
    }
    if (state.selectedSubmissionId === 'new') {
      normalized.dealroomInterestId = 'new'
    }
    return submissionApi.updateSubmission(normalized).then(res => {
      if (res.data.dealroomInterestId) {
        commit(SET_SUBMISSION_ID, res.data.dealroomInterestId)
      }
    })
  },
  changeSubmissionStatus({ commit, getters }, payload) {
    const { deal, state, reason, type } = payload
    const proposal = {
      ...deal.formatted,
      dealroomInterestId: deal.dealroomInterestId,
      isComplete: deal.isComplete,
      type,
      step: 2,
    }
    proposal.interest.submissionState = state
    proposal.interest.stateReason = reason

    return submissionApi.updateSubmission(proposal).then(res => {
      let submissions = getters.submissions.filter(s => {
        if (s.dealroomInterestId === proposal.dealroomInterestId) {
          return false
        }
        return true
      })
      submissions = [{ ...res.data, formatted: proposal }].concat(submissions)
      commit(SET_SUBMISSIONS, submissions)
    })
  },
  getOpportunities({ commit }) {
    return opportunitiesApi.getOpportunities().then(res => {
      const normalized = res.data.items.reduce((result, item) => {
        result[item.id] = {
          closed: item.status === 'closed',
          fullTitle: item.title,
          preRelease: item.status === 'prerelease',
          ...item,
        }
        return result
      }, {})
      commit(SET_OPPORTUNITIES, normalized)
    })
  },
  selectPlatform({ commit }, payload) {
    commit(SELECT_PLATFORM, payload)
  },
  selectSubmissionCompany({ commit }, payload) {
    commit(SET_COMPANY_DATA, payload)
  },
}

const getters = {
  platforms: state => {
    const codes = Object.keys(state.platforms).filter(
      k =>
        !state.platforms[k].preRelease ||
        (state.platforms[k].preRelease && RELEASE_STAGE !== 'production')
    )
    return _.pick(state.platforms, codes)
  },
  latestSubmission: state => state.latestSubmission,
  selectedSubmissionId: state => state.selectedSubmissionId,
  platformTitle: (state, getters) => id => {
    return _.get(getters.platforms[id], 'title') || ''
  },
  platformType: (state, getters) => id => {
    return _.get(getters.platforms[id], 'type') || ''
  },
  selectedPlatformType: (state, getters) => {
    const id = getters.selectedPlatform
    return _.get(getters.platforms[id], 'type') || ''
  },
  selectedPlatform: state => state.selectedPlatform,
  selectedCompanyData: state => state.selectedCompanyData,
  selectedCompanySubmissions: (state, getters) => {
    const selectedCompanyId = _.get(
      getters.selectedCompanyData,
      'selectedCompany._id'
    )
    if (selectedCompanyId) {
      return getters.submissionsByCompany(
        getters.selectedCompanyData.selectedCompany
      )
    }
    return []
  },
  submissions: state => state.submissions,
  internalSubmissions: state => state.internalSubmissions,
  isInternalLoaded: state => state.isInternalLoaded,
  userCompanies: state => {
    const allCompanies = state.submissions
      .filter(submission => {
        if (submission.type === 'dealbook.submission') {
          const isProfileExists = state.submissions.find(i => {
            return (
              i.opportunityCompanyId === submission.opportunityCompanyId &&
              i.type === 'company.profile'
            )
          })
          if (isProfileExists) {
            return false
          }
          return true
        }
        return true
      })
      .map(submission => {
        if (submission.opportunityCompanyId) {
          const company = {
            ...submission.formatted.company,
            _id: submission.companyId,
            companyProfileId: null,
            opportunityCompanyId: submission.opportunityCompanyId,
          }
          if (
            submission.type === 'company.profile' &&
            submission.dealroomInterestId
          ) {
            company.companyProfileId = submission.dealroomInterestId
          }
          return company
        }
      })
      .filter(v => v)
    return _.uniqBy(allCompanies, 'opportunityCompanyId')
  },
  submissionsByCompany: (state, getters) => company => {
    return getters.submissions.filter(item => {
      if (company._id && item.companyId) {
        return item.companyId === company._id
      }
      if (company.displayName) {
        return item.companyDisplayName === company.displayName
      }
    })
  },
  orderedPlatforms: (state, getters) => {
    return Object.values(getters.platforms)
      .map(platform => {
        return { order: platform.order, ...platform }
      })
      .sort((a, b) => a.order - b.order)
  },
  selectablePlatforms: (state, getters) => {
    return getters.orderedPlatforms.filter(p => !p.noSubmission)
  },
  previousSubmission: (state, getters) => {
    const companyData = getters.selectedCompanyData
    if (companyData.isNewCompany) {
      return null
    }
    const submissions = getters.submissionsByCompany(
      companyData.selectedCompany
    )
    if (submissions.length) {
      return submissions[0]
    }
    return null
  },
}

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
}
